import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import { 
  Grid, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { DepartmentList } from './DepartmentList';
import { EmployeeTable } from './EmployeeTable';
import { Employee, Department, SortField, SortOrder } from '../../types';

interface SendMessageResponse {
  details?: {
    batchId: string;
    totalRecipients: number;
    messageLength: number;
    multipleMessages: boolean;
  };
}

interface MainViewProps {
  onSendMessage?: (message: string) => Promise<SendMessageResponse>;
  employees: Employee[];
  departments: Map<string, Department>;
  selectedDepartments: string[];
  selectedEmployees: string[];
  searchQuery: string;
  sortField: SortField;
  sortOrder: SortOrder;
  loading?: boolean;
  onDepartmentSelect: (deptName: string) => void;
  onEmployeeSelect: (employeeId: string) => void;
  onSearchChange: (query: string) => void;
  onSort: (field: SortField) => void;
  onSaveListClick: () => void;
  onSelectAllEmployees: (checked: boolean) => void;
}

export const MainView: React.FC<MainViewProps> = ({
  employees,
  departments,
  selectedDepartments,
  selectedEmployees,
  searchQuery,
  sortField,
  sortOrder,
  loading = false,
  onDepartmentSelect,
  onEmployeeSelect,
  onSearchChange,
  onSort,
  onSaveListClick,
  onSelectAllEmployees,
  onSendMessage,
}) => {
  const [message, setMessage] = useState('');
  const [sendingStatus, setSendingStatus] = useState<{
    open: boolean;
    batchId?: string;
    totalRecipients?: number;
    delivered: number;
    failed: number;
    pending: number;
  }>({
    open: false,
    delivered: 0,
    failed: 0,
    pending: 0,
  });
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const updateMessageStatus = async (batchId: string) => {
    try {
      const response = await axiosInstance.get('/message-history');
      const history = response.data;
      
      // Filter messages for current batch
      const batchMessages = history.filter((msg: any) => msg.batchId === batchId);
      
      // Count statuses
      const delivered = batchMessages.filter((msg: any) => msg.status === 'delivered')
        .reduce((acc: number, msg: any) => acc + msg.recipients.length, 0);
      const failed = batchMessages.filter((msg: any) => msg.status === 'failed')
        .reduce((acc: number, msg: any) => acc + msg.recipients.length, 0);
      
      setSendingStatus(prev => ({
        ...prev,
        delivered,
        failed,
        pending: (prev.totalRecipients || 0) - delivered - failed,
      }));

      // Stop polling if all messages are processed
      if (delivered + failed === (sendingStatus.totalRecipients || 0)) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch message status:', error);
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() && onSendMessage) {
      try {
        // Open status dialog before sending
        setSendingStatus({
          open: true,
          totalRecipients: selectedEmployees.length,
          delivered: 0,
          failed: 0,
          pending: selectedEmployees.length,
        });

        const response = await onSendMessage(message);
        const batchId = response?.details?.batchId;
        
        if (batchId) {
          setSendingStatus(prev => ({
            ...prev,
            batchId,
          }));

          // Start polling for updates
          const interval = setInterval(() => {
            updateMessageStatus(batchId);
          }, 2000); // Poll every 2 seconds
          
          setPollingInterval(interval);
        }

        setMessage('');
      } catch (error) {
        console.error('Failed to send message:', error);
        setSendingStatus(prev => ({
          ...prev,
          failed: prev.totalRecipients || 0,
          pending: 0,
        }));
      }
    }
  };

  const handleCloseStatus = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setSendingStatus({
      open: false,
      delivered: 0,
      failed: 0,
      pending: 0,
    });
  };

  return (
    <>
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <DepartmentList
          departments={departments}
          selectedDepartments={selectedDepartments}
          onDepartmentSelect={onDepartmentSelect}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <EmployeeTable
          employees={employees}
          selectedEmployees={selectedEmployees}
          searchQuery={searchQuery}
          sortField={sortField}
          sortOrder={sortOrder}
          loading={loading}
          onEmployeeSelect={onEmployeeSelect}
          onSearchChange={onSearchChange}
          onSort={onSort}
          onSaveListClick={onSaveListClick}
          onSelectAllEmployees={onSelectAllEmployees}
        />
      </Grid>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Emergency Message
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Send an emergency SMS to all selected recipients
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Type your emergency message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={message.length > 160}
                helperText={message.length > 160 ? 'Message exceeds 160 characters limit' : `${message.length}/160 characters`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleSendMessage}
                disabled={!message.trim() || selectedEmployees.length === 0 || loading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  textTransform: 'none',
                }}
              >
                {loading ? 'Sending...' : `Send Message to ${selectedEmployees.length} Selected Recipients`}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>

      <Dialog
        open={sendingStatus.open}
        onClose={handleCloseStatus}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sending Emergency Messages
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Total Recipients: {sendingStatus.totalRecipients}
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary={`Delivered: ${sendingStatus.delivered}`}
                  secondary={
                    sendingStatus.delivered > 0 
                      ? `${Math.round((sendingStatus.delivered / (sendingStatus.totalRecipients || 1)) * 100)}%`
                      : null
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary={`Failed: ${sendingStatus.failed}`}
                  secondary={
                    sendingStatus.failed > 0
                      ? `${Math.round((sendingStatus.failed / (sendingStatus.totalRecipients || 1)) * 100)}%`
                      : null
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary={`Pending: ${sendingStatus.pending}`}
                  secondary={
                    sendingStatus.pending > 0
                      ? `${Math.round((sendingStatus.pending / (sendingStatus.totalRecipients || 1)) * 100)}%`
                      : null
                  }
                />
              </ListItem>
            </List>

            {sendingStatus.pending > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}

            {sendingStatus.pending === 0 && (
              <Alert 
                severity={sendingStatus.failed > 0 ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                {sendingStatus.failed > 0
                  ? `Message sending completed with ${sendingStatus.failed} failed deliveries`
                  : "All messages delivered successfully"}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseStatus}
            disabled={sendingStatus.pending > 0}
          >
            {sendingStatus.pending > 0 ? "Sending..." : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MainView;
